/* PØM Size Guide: Fit Map + Your Fit.
 *
 * Body data: ANSUR II (2012 Anthropometric Survey of U.S. Army Personnel),
 * public release, tools.openlab.psu.edu/publicData. 4,082 male / 1,986 female
 * subjects. Every number in size-guide-ansur-data.json is computed from the
 * raw CSVs by scripts/compute-ansur-summary.mjs in this repo. Nothing is
 * estimated from memory.
 *
 * Measurement: all math in inches. Vertical placement is "inches from the top of
 * the head" = stature x (1 - landmark ratio), so SVG y composes directly.
 *
 * Fit math (spec):
 *   ease  = garment chest flat x 2 - body chest circumference
 *   hem   = body HPS (cervicale) from top + garment Length
 *   cuff  = body shoulder point (acromion) + drop offset + Sleeve, where
 *           drop offset = (garment Shoulder - body biacromial breadth) / 2,
 *           clamped at 0, measured straight down the arm
 *   pants = Hip x 2 vs body hip circumference gives the fit band; the waist
 *           is elastic and is not compared; hem = waist line + Length
 *
 * The garment is drawn at its FLAT width (the number the customer can
 * measure) over a body drawn at its breadth. The +-1 in supplier tolerance
 * is the shaded band around the garment outline.
 */
(function () {
  'use strict';

  // Fit-band thresholds, inches of ease. One place, not hardcoded per-copy.
  var FIT_BANDS = [
    { max: 2, label: 'SNUG' },
    { max: 4, label: 'REGULAR' },
    { max: 6, label: 'RELAXED' },
    { max: Infinity, label: 'OVERSIZED' },
  ];
  function fitBandFor(easeIn) {
    for (var i = 0; i < FIT_BANDS.length; i++) {
      if (easeIn < FIT_BANDS[i].max) return FIT_BANDS[i].label;
    }
    return 'OVERSIZED';
  }

  var TOLERANCE_IN = 1; // supplier tolerance, either way
  var AT_THRESHOLD_IN = 0.75; // closer than this to a landmark reads as "at"
  var STORAGE_KEY = 'pom-size-guide-your-fit';
  var IN_TO_CM = 2.54;
  var LB_TO_KG = 0.45359237;

  function safeLocalGet(key) {
    try {
      var v = localStorage.getItem(key);
      return v ? JSON.parse(v) : null;
    } catch (e) {
      return null;
    }
  }
  function safeLocalSet(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode or disabled storage: skip, it is a convenience only */
    }
  }

  var ansurCache = null;
  function loadAnsur(url) {
    if (ansurCache) return Promise.resolve(ansurCache);
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        ansurCache = data;
        return data;
      });
  }

  function colIndex(columns, name) {
    for (var i = 0; i < columns.length; i++) {
      if (String(columns[i]).trim().toLowerCase() === name.toLowerCase()) return i;
    }
    return -1;
  }
  function num(v) {
    if (v == null || v === '') return null;
    var n = Number(v);
    return isNaN(n) ? null : n;
  }
  function round1(n) { return Math.round(n * 10) / 10; }

  // ── Measurement + copy helpers ────────────────────────────────────────────────
  function fmtLen(inches, unit, decimals) {
    if (inches == null || isNaN(inches)) return '';
    var v = unit === 'cm' ? inches * IN_TO_CM : inches;
    var d = decimals == null ? (unit === 'cm' ? 0 : 1) : decimals;
    var s = (Math.round(v * Math.pow(10, d)) / Math.pow(10, d)).toString();
    return s + ' ' + unit;
  }
  function fmtSigned(inches, unit) {
    var s = fmtLen(Math.abs(inches), unit);
    return (inches < 0 ? '-' : '+') + s;
  }
  function fmtHeight(inches, unit) {
    if (unit === 'cm') return Math.round(inches * IN_TO_CM) + ' cm';
    var ft = Math.floor(inches / 12);
    var inch = Math.round(inches - ft * 12);
    if (inch === 12) { ft += 1; inch = 0; }
    return ft + "'" + inch + '"';
  }
  function fmtWeight(lb, unit) {
    return unit === 'cm' ? Math.round(lb * LB_TO_KG) + ' kg' : Math.round(lb) + ' lb';
  }
  // "at the hip" / "2 in above the navel" / "3 in below the hip"
  function relativeTo(deltaIn, name, unit, belowWord, aboveWord) {
    if (Math.abs(deltaIn) < AT_THRESHOLD_IN) return 'at the ' + name;
    var word = deltaIn > 0 ? (belowWord || 'below') : (aboveWord || 'above');
    return fmtLen(Math.abs(deltaIn), unit) + ' ' + word + ' the ' + name;
  }

  // ── Body model ──────────────────────────────────────────────────────────
  // Every body carries stature, circumferences, breadths and the landmark
  // ratios. fromTop(name) gives inches from the top of the head.
  function makeBody(statureIn, fields, ratios) {
    var b = {
      statureIn: statureIn,
      chestIn: fields.chestIn,
      waistIn: fields.waistIn,
      hipIn: fields.hipIn,
      bideltoidIn: fields.bideltoidIn,
      biacromialIn: fields.biacromialIn,
      chestBreadthIn: fields.chestBreadthIn,
      waistBreadthIn: fields.waistBreadthIn,
      hipBreadthIn: fields.hipBreadthIn,
      ratios: ratios,
    };
    b.fromTop = function (name) { return b.statureIn * (1 - b.ratios[name]); };
    return b;
  }
  function meanBody(g) {
    return makeBody(g.meanStatureIn, {
      chestIn: g.meanChestIn,
      waistIn: g.meanWaistIn,
      hipIn: g.meanHipIn,
      bideltoidIn: g.meanBideltoidIn,
      biacromialIn: g.meanBiacromialIn,
      chestBreadthIn: g.meanChestBreadthIn,
      waistBreadthIn: g.meanWaistBreadthIn,
      hipBreadthIn: g.meanHipBreadthIn,
    }, g.landmarkRatios);
  }
  // Regression on height + weight; slim/broad presets shift by the residual
  // 25th / 75th percentile of that regression (computed from the dataset).
  function regress(reg, heightIn, weightLb, preset) {
    var v = reg.intercept + reg.perHeightIn * heightIn + reg.perWeightLb * weightLb;
    if (preset === 'slim') v += reg.residualP25In;
    if (preset === 'broad') v += reg.residualP75In;
    return v;
  }
  function estimateBody(g, heightIn, weightLb, preset) {
    var r = g.regression;
    return makeBody(heightIn, {
      chestIn: regress(r.chestCircumference, heightIn, weightLb, preset),
      waistIn: regress(r.waistCircumference, heightIn, weightLb, preset),
      hipIn: regress(r.hipCircumference, heightIn, weightLb, preset),
      bideltoidIn: regress(r.bideltoidBreadth, heightIn, weightLb, preset),
      biacromialIn: regress(r.biacromialBreadth, heightIn, weightLb, preset),
      chestBreadthIn: regress(r.chestBreadth, heightIn, weightLb, preset),
      waistBreadthIn: regress(r.waistBreadth, heightIn, weightLb, preset),
      hipBreadthIn: regress(r.hipBreadth, heightIn, weightLb, preset),
    }, g.landmarkRatios);
  }

  // ── Chart parsing ───────────────────────────────────────────────────────
  // Columns are read BY NAME. Supplier column orders differ.
  function parseChart(chart) {
    var columns = chart.columns || [];
    var rows = chart.rows || [];
    var idx = {
      size: colIndex(columns, 'Size'),
      length: colIndex(columns, 'Length'),
      chest: colIndex(columns, 'Chest'),
      shoulder: colIndex(columns, 'Shoulder'),
      sleeve: colIndex(columns, 'Sleeve'),
      waist: colIndex(columns, 'Waist'),
      hip: colIndex(columns, 'Hip'),
    };
    var isPants = idx.chest === -1 && (idx.waist !== -1 || idx.hip !== -1);
    var sizes = rows.map(function (row) {
      var get = function (i) { return i === -1 ? null : num(row[i]); };
      return {
        size: idx.size !== -1 ? row[idx.size] : row[0],
        isPants: isPants,
        lengthIn: get(idx.length),
        chestIn: get(idx.chest),
        shoulderIn: get(idx.shoulder),
        sleeveIn: get(idx.sleeve),
        waistIn: get(idx.waist),
        hipIn: get(idx.hip),
      };
    });
    return { sizes: sizes, isPants: isPants };
  }

  // Per-size fit numbers for one body. Returns a new object per size.
  function fitFor(s, body) {
    var f = { size: s.size, isPants: s.isPants, spec: s };
    if (s.isPants) {
      if (s.hipIn != null) {
        f.easeIn = round1(s.hipIn * 2 - body.hipIn);
        f.easeLabel = 'hip';
        f.fitBand = fitBandFor(f.easeIn);
      }
      // Waist is intentionally not compared: elastic waistband, the flat
      // number is meaningless against a body. Hip carries the fit band.
      if (s.lengthIn != null) {
        f.waistFromTopIn = body.fromTop('waistOmphalion');
        f.hemFromTopIn = f.waistFromTopIn + s.lengthIn;
        f.hemDeltaIn = f.hemFromTopIn - body.fromTop('ankle'); // + = below the ankle
      }
    } else {
      if (s.chestIn != null) {
        f.easeIn = round1(s.chestIn * 2 - body.chestIn);
        f.easeLabel = 'chest';
        f.fitBand = fitBandFor(f.easeIn);
      }
      if (s.lengthIn != null) {
        f.hemFromTopIn = body.fromTop('cervicale') + s.lengthIn;
        var navel = body.fromTop('waistOmphalion');
        var hip = body.fromTop('buttock');
        // nearest of navel / hip; beyond the hip always reads against the hip
        if (f.hemFromTopIn <= (navel + hip) / 2) {
          f.hemLandmark = 'navel';
          f.hemDeltaIn = f.hemFromTopIn - navel;
        } else {
          f.hemLandmark = 'hip';
          f.hemDeltaIn = f.hemFromTopIn - hip;
        }
      }
      if (s.sleeveIn != null) {
        var shoulderRef = s.shoulderIn != null ? s.shoulderIn : body.biacromialIn;
        f.dropOffsetIn = Math.max(0, (shoulderRef - body.biacromialIn) / 2);
        f.cuffFromTopIn = body.fromTop('acromion') + f.dropOffsetIn + s.sleeveIn;
        var elbow = body.fromTop('elbow');
        var wrist = body.fromTop('wrist');
        if (f.cuffFromTopIn <= (elbow + wrist) / 2) {
          f.sleeveLandmark = 'elbow';
          f.sleeveDeltaIn = f.cuffFromTopIn - elbow;
        } else {
          f.sleeveLandmark = 'wrist';
          f.sleeveDeltaIn = f.cuffFromTopIn - wrist;
        }
      }
    }
    return f;
  }

  function fitLines(f, body, unit) {
    var lines = [];
    if (f.fitBand) lines.push('<strong>' + f.fitBand + '</strong>, ' + fmtSigned(f.easeIn, unit) + ' ' + f.easeLabel);
    if (f.hemDeltaIn != null) {
      lines.push(f.isPants
        ? 'Hem: ' + relativeTo(f.hemDeltaIn, 'ankle', unit)
        : 'Hem: ' + relativeTo(f.hemDeltaIn, f.hemLandmark, unit));
    }
    if (f.sleeveDeltaIn != null) {
      lines.push('Sleeve ends ' + relativeTo(f.sleeveDeltaIn, f.sleeveLandmark, unit, f.sleeveLandmark === 'wrist' ? 'past' : 'below'));
    }
    return lines;
  }

  // ── SVG figure drawing ──────────────────────────────────────────────────
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var VIEW_W = 160;
  var VIEW_H = 320;

  function el(tag, attrs) {
    var e = document.createElementNS(SVG_NS, tag);
    for (var k in attrs) e.setAttribute(k, attrs[k]);
    return e;
  }
  function pts(arr) {
    return arr.map(function (p) { return p[0].toFixed(1) + ',' + p[1].toFixed(1); }).join(' ');
  }
  function pathD(arr) {
    return 'M ' + arr.map(function (p) { return p[0].toFixed(1) + ' ' + p[1].toFixed(1); }).join(' L ') + ' Z';
  }

  // Draws the body silhouette, then (optionally) one garment over it.
  // garment: the size spec {chestIn, lengthIn, shoulderIn, sleeveIn,
  // waistIn, hipIn, isPants} or null.
  function drawFigure(svg, body, garment, opts) {
    opts = opts || {};
    var tolLabel = opts.unit === 'cm' ? '\u00b12.5 cm' : '\u00b11 in';
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    svg.setAttribute('viewBox', '0 0 ' + VIEW_W + ' ' + VIEW_H);

    var pxPerIn = (VIEW_H * 0.92) / body.statureIn;
    var cx = VIEW_W / 2;
    var topPad = VIEW_H * 0.04;
    var Y = function (fromTopIn) { return topPad + fromTopIn * pxPerIn; };
    var P = function (inches) { return inches * pxPerIn; };

    var cervicaleY = Y(body.fromTop('cervicale'));
    var acromionY = Y(body.fromTop('acromion'));
    var chestY = Y(body.fromTop('chest'));
    var navelY = Y(body.fromTop('waistOmphalion'));
    var hipY = Y(body.fromTop('buttock'));
    var crotchY = Y(body.fromTop('crotch'));
    var kneeY = Y(body.fromTop('knee'));
    var ankleY = Y(body.fromTop('ankle'));
    var floorY = Y(body.statureIn);
    var wristY = Y(body.fromTop('wrist'));

    // head + neck: the head/neck block is stature minus cervicale height
    var headNeckIn = body.fromTop('cervicale');
    var headR = P(headNeckIn * 0.4);
    var headCy = topPad + headR;
    var neckHalf = P(2.3);
    var g = el('g', { class: 'sg-body' });
    g.appendChild(el('circle', { cx: cx, cy: headCy, r: headR, class: 'sg-body-fill' }));
    g.appendChild(el('rect', { x: cx - neckHalf, y: headCy + headR * 0.8, width: neckHalf * 2, height: Math.max(2, cervicaleY - (headCy + headR * 0.8) + 3), class: 'sg-body-fill' }));

    // torso: neck base -> acromion -> deltoid -> chest -> waist -> hip -> crotch
    var halfBiac = P(body.biacromialIn / 2);
    var halfBidel = P(body.bideltoidIn / 2);
    var halfChest = P(body.chestBreadthIn / 2);
    var halfWaist = P(body.waistBreadthIn / 2);
    var halfHip = P(body.hipBreadthIn / 2);
    var deltoidY = acromionY + P(1.6);
    var torso = [
      [cx - neckHalf, cervicaleY], [cx - halfBiac, acromionY], [cx - halfBidel, deltoidY],
      [cx - halfChest, chestY], [cx - halfWaist, navelY], [cx - halfHip, hipY], [cx - halfHip * 0.96, crotchY],
      [cx + halfHip * 0.96, crotchY], [cx + halfHip, hipY], [cx + halfWaist, navelY], [cx + halfChest, chestY],
      [cx + halfBidel, deltoidY], [cx + halfBiac, acromionY], [cx + neckHalf, cervicaleY],
    ];
    g.appendChild(el('polygon', { points: pts(torso), class: 'sg-body-fill' }));

    // legs: hip -> knee -> ankle, each leg
    var legTopOuter = halfHip * 0.96, legTopInner = P(0.4);
    var kneeOuter = halfHip * 0.62, kneeInner = P(0.9);
    var ankleOuter = halfHip * 0.42, ankleInner = P(1.2);
    [-1, 1].forEach(function (side) {
      var leg = [
        [cx + side * legTopInner, crotchY], [cx + side * legTopOuter, crotchY],
        [cx + side * kneeOuter, kneeY], [cx + side * ankleOuter, ankleY],
        [cx + side * ankleOuter, floorY], [cx + side * ankleInner, floorY],
        [cx + side * ankleInner, ankleY], [cx + side * kneeInner, kneeY],
      ];
      g.appendChild(el('polygon', { points: pts(leg), class: 'sg-body-fill' }));
    });

    // arms: hang from the deltoid, slightly outward, to the wrist
    var armTopHalf = P(1.9), armBottomHalf = P(1.2);
    var wristX = function (side) { return cx + side * (halfBidel + P(1.2)); };
    [-1, 1].forEach(function (side) {
      var ax = cx + side * (halfBidel - armTopHalf * 0.55);
      var arm = [
        [ax - armTopHalf, acromionY + P(0.6)], [ax + armTopHalf, acromionY + P(0.6)],
        [wristX(side) + armBottomHalf, wristY], [wristX(side) - armBottomHalf, wristY],
      ];
      g.appendChild(el('polygon', { points: pts(arm), class: 'sg-body-fill' }));
    });
    svg.appendChild(g);

    // landmark ticks (subtle): navel, hip, ankle
    var ticks = el('g', { class: 'sg-landmarks' });
    [navelY, hipY, ankleY].forEach(function (y) {
      ticks.appendChild(el('line', { x1: cx - halfHip - 8, y1: y, x2: cx - halfHip - 3, y2: y }));
    });
    svg.appendChild(ticks);

    if (!garment) return;

    var tol = P(TOLERANCE_IN);
    var gg = el('g', { class: 'sg-garment' });

    if (garment.isPants) {
      if (garment.lengthIn == null) return;
      var waistY = navelY;
      var hemY = Y(body.fromTop('waistOmphalion') + garment.lengthIn);
      var halfWaistG = P(Math.max(garment.waistIn != null ? garment.waistIn : body.waistBreadthIn, body.waistBreadthIn) / 2);
      var halfHipG = P((garment.hipIn != null ? garment.hipIn : body.hipBreadthIn) / 2);
      var legHem = halfHipG * 0.62;
      var legOuterHem = halfHipG * 0.9;
      var innerHem = legOuterHem - legHem;
      var pantsPath = function (dx, dyHem) {
        var hy = hemY + dyHem;
        return [
          [cx - halfWaistG - dx, waistY], [cx + halfWaistG + dx, waistY],
          [cx + halfHipG + dx, hipY], [cx + legOuterHem + dx, hy], [cx + innerHem - dx, hy],
          [cx, crotchY + P(0.5) + dx], [cx - innerHem + dx, hy], [cx - legOuterHem - dx, hy],
          [cx - halfHipG - dx, hipY],
        ];
      };
      gg.appendChild(el('path', {
        d: pathD(pantsPath(tol, tol)) + ' ' + pathD(pantsPath(-tol, -tol)),
        'fill-rule': 'evenodd', class: 'sg-tolerance-band',
      }));
      gg.appendChild(el('polygon', { points: pts(pantsPath(0, 0)), class: 'sg-garment-outline' }));
      gg.appendChild(el('line', { x1: cx - legOuterHem - 6, y1: hemY, x2: cx + legOuterHem + 6, y2: hemY, class: 'sg-hem-line' }));
      var ptl = el('text', { x: cx + legOuterHem + tol + 4, y: hemY + 5, class: 'sg-tol-label' });
      ptl.textContent = tolLabel;
      gg.appendChild(ptl);
      svg.appendChild(gg);
      return;
    }

    if (garment.lengthIn == null) return;
    var halfChestG = P((garment.chestIn != null ? garment.chestIn : body.chestBreadthIn) / 2);
    var shoulderRef = garment.shoulderIn != null ? garment.shoulderIn : body.biacromialIn;
    var dropIn = Math.max(0, (shoulderRef - body.biacromialIn) / 2);
    var halfShoulderG = P(shoulderRef / 2);
    var hemTopY = Y(body.fromTop('cervicale') + garment.lengthIn);
    // arm axis from the acromion to the wrist, per side
    var armDir = function (side) {
      var x0 = cx + side * halfBiac, y0 = acromionY;
      var x1 = wristX(side), y1 = wristY;
      var len = Math.sqrt((x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0));
      return { x0: x0, y0: y0, ux: (x1 - x0) / len, uy: (y1 - y0) / len };
    };
    // the shoulder seam sits dropIn down the arm from the acromion
    var seam = function (side) {
      var d = armDir(side);
      var t = P(dropIn);
      return [d.x0 + d.ux * t, d.y0 + d.uy * t];
    };
    var seamL = seam(-1), seamR = seam(1);
    // never narrower than the garment's own shoulder width
    seamL[0] = Math.min(seamL[0], cx - halfShoulderG);
    seamR[0] = Math.max(seamR[0], cx + halfShoulderG);

    var bodyPath = function (dx, dy) {
      return [
        [seamL[0] - dx * 0.4, seamL[1] - dy * 0.4], [cx - neckHalf, cervicaleY + P(0.8) - dy * 0.4],
        [cx + neckHalf, cervicaleY + P(0.8) - dy * 0.4], [seamR[0] + dx * 0.4, seamR[1] - dy * 0.4],
        [cx + halfChestG + dx, chestY], [cx + halfChestG + dx, hemTopY + dy],
        [cx - halfChestG - dx, hemTopY + dy], [cx - halfChestG - dx, chestY],
      ];
    };
    gg.appendChild(el('path', {
      d: pathD(bodyPath(tol, tol)) + ' ' + pathD(bodyPath(-tol, -tol)),
      'fill-rule': 'evenodd', class: 'sg-tolerance-band',
    }));
    gg.appendChild(el('polygon', { points: pts(bodyPath(0, 0)), class: 'sg-garment-outline' }));
    gg.appendChild(el('line', { x1: cx - halfChestG - 6, y1: hemTopY, x2: cx + halfChestG + 6, y2: hemTopY, class: 'sg-hem-line' }));
    var ttl = el('text', { x: cx + halfChestG + tol + 4, y: hemTopY + 5, class: 'sg-tol-label' });
    ttl.textContent = tolLabel;
    gg.appendChild(ttl);

    if (garment.sleeveIn != null) {
      [-1, 1].forEach(function (side) {
        var d = armDir(side);
        var t0 = P(dropIn);
        var t1 = P(dropIn + garment.sleeveIn);
        var s0 = [d.x0 + d.ux * t0, d.y0 + d.uy * t0];
        var s1 = [d.x0 + d.ux * t1, d.y0 + d.uy * t1];
        var nx = -d.uy, ny = d.ux; // perpendicular to the arm
        var w0 = P(2.9), w1 = P(2.2);
        var sleeve = [
          [s0[0] + nx * w0, s0[1] + ny * w0], [s1[0] + nx * w1, s1[1] + ny * w1],
          [s1[0] - nx * w1, s1[1] - ny * w1], [s0[0] - nx * w0, s0[1] - ny * w0],
        ];
        gg.appendChild(el('polygon', { points: pts(sleeve), class: 'sg-garment-outline sg-sleeve' }));
        gg.appendChild(el('line', {
          x1: s1[0] + nx * (w1 + 3), y1: s1[1] + ny * (w1 + 3), x2: s1[0] - nx * (w1 + 3), y2: s1[1] - ny * (w1 + 3),
          class: 'sg-hem-line',
        }));
      });
    }
    svg.appendChild(gg);
  }

  // ── Size recommendation ─────────────────────────────────────────────────
  // With a garment the customer owns: the size whose flat measurement is
  // closest to it (chest for tops, hip for pants). Otherwise: the size whose
  // ease on THIS body is closest to the ease the chart's middle size gives
  // the average body, i.e. the fit the chart was cut for. Ties go up a size.
  function recommend(parsed, userBody, popBody, ownFlatIn) {
    var sizes = parsed.sizes;
    var key = parsed.isPants ? 'hipIn' : 'chestIn';
    var best = null, bestDiff = Infinity;
    var consider = function (s, diff) {
      if (diff < bestDiff - 1e-9 || (Math.abs(diff - bestDiff) < 1e-9 && best && s[key] > best[key])) { bestDiff = diff; best = s; }
    };
    var withMeasure = sizes.filter(function (s) { return s[key] != null; });
    if (!withMeasure.length) return null;
    if (ownFlatIn != null) {
      withMeasure.forEach(function (s) { consider(s, Math.abs(s[key] - ownFlatIn)); });
      return best ? { size: best, byOwnGarment: true } : null;
    }
    var anchor = withMeasure[Math.floor((withMeasure.length - 1) / 2)];
    var designEase = fitFor(anchor, popBody).easeIn;
    withMeasure.forEach(function (s) { consider(s, Math.abs(fitFor(s, userBody).easeIn - designEase)); });
    return best ? { size: best, byOwnGarment: false } : null;
  }

  // ── One size-guide instance ─────────────────────────────────────────────
  // The size currently selected on the product page. Reads the live picker
  // first (covers a size change that did not trigger a section re-render),
  // then the server-rendered data-selected-size on the root.
  function selectedSizeFor(root) {
    var section = root.closest('.shopify-section') || document;
    var selectors = section.querySelectorAll('.option-selector[data-option]');
    for (var i = 0; i < selectors.length; i++) {
      if (String(selectors[i].getAttribute('data-option') || '').trim().toLowerCase() !== 'size') continue;
      var radio = selectors[i].querySelector('input.js-option:checked');
      if (radio) return String(radio.value || radio.getAttribute('data-value') || '').trim();
      var opt = selectors[i].querySelector('.js-option[aria-selected="true"]');
      if (opt) return String(opt.getAttribute('data-value') || opt.textContent || '').trim();
    }
    return String(root.getAttribute('data-selected-size') || '').trim();
  }
  function sameSize(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  }

  // Symmetry replaces the whole variant-picker block (and this modal inside
  // it) with fresh markup on every size change, so a root can appear at any
  // time. initInstance is idempotent: a root is set up once, marked, and
  // later scans skip it.
  function initInstance(root) {
    if (root.getAttribute('data-sg-init') === '1') return;
    var dataScript = root.querySelector('.sg-data');
    if (!dataScript) return;
    var chart;
    try { chart = JSON.parse(dataScript.textContent); } catch (e) { return; }
    root.setAttribute('data-sg-init', '1');
    var ansurUrl = root.getAttribute('data-ansur-url');
    var isWomens = root.getAttribute('data-womens') === 'true';
    var parsed = parseChart(chart);

    var fitMapGrid = root.querySelector('.sg-fitmap-grid');
    var yourFitBody = root.querySelector('.sg-yourfit-body');
    // userRanFit flips the first time the customer touches Your Fit; until
    // then the highlighted box is the size selected on the page.
    var state = { unit: 'in', userRanFit: false, rec: null };

    loadAnsur(ansurUrl).then(function (ansur) {
      var g = isWomens ? ansur.female : ansur.male;
      var popBody = meanBody(g);
      var boxes = [];

      function highlightedSize() {
        if (state.userRanFit && state.rec) return String(state.rec.size.size);
        return selectedSizeFor(root);
      }
      function applyHighlight() {
        var want = highlightedSize();
        boxes.forEach(function (box) {
          box.classList.toggle('is-recommended', !!want && sameSize(box.dataset.size, want));
        });
      }
      root.__sgApplyHighlight = applyHighlight;

      function renderFitMap() {
        if (!fitMapGrid) return;
        fitMapGrid.innerHTML = '';
        boxes = [];
        parsed.sizes.forEach(function (s) {
          var f = fitFor(s, popBody);
          var box = document.createElement('div');
          box.className = 'sg-fitmap-box';
          box.dataset.size = s.size;

          var svg = el('svg', { class: 'sg-fitmap-svg', 'aria-hidden': 'true' });
          drawFigure(svg, popBody, s, { unit: state.unit });

          var label = document.createElement('div');
          label.className = 'sg-fitmap-size';
          label.textContent = s.size;

          var meta = document.createElement('div');
          meta.className = 'sg-fitmap-meta';
          meta.innerHTML = fitLines(f, popBody, state.unit).map(function (l) { return '<span>' + l + '</span>'; }).join('');

          var calcLabel = document.createElement('div');
          calcLabel.className = 'sg-fitmap-calc-label';
          calcLabel.textContent = 'Calculated fit, not a photo';

          box.appendChild(svg);
          box.appendChild(label);
          box.appendChild(meta);
          box.appendChild(calcLabel);
          fitMapGrid.appendChild(box);
          boxes.push(box);
        });
        applyHighlight();
      }
      renderFitMap();

      var yourFit = yourFitBody ? setupYourFit(yourFitBody, parsed, g, popBody, state, function (rec, byUser) {
        state.rec = rec;
        if (byUser) state.userRanFit = true;
        applyHighlight();
      }) : null;

      // The IN / CM toggle lives in the chart section; follow it everywhere.
      root.addEventListener('click', function (evt) {
        var btn = evt.target.closest('.size-guide-unit-btn');
        if (!btn || !btn.dataset.unit || btn.dataset.unit === state.unit) return;
        state.unit = btn.dataset.unit;
        renderFitMap();
        if (yourFit) yourFit.unitChanged();
      });
    }).catch(function () {
      if (fitMapGrid) fitMapGrid.innerHTML = '<p class="sg-error">Fit map unavailable right now.</p>';
    });
  }

  function setupYourFit(container, parsed, g, popBody, state, onRecommend) {
    var saved = safeLocalGet(STORAGE_KEY) || {};
    var heightSlider = container.querySelector('[data-sg="height"]');
    var weightSlider = container.querySelector('[data-sg="weight"]');
    var heightOut = container.querySelector('[data-sg="height-out"]');
    var weightOut = container.querySelector('[data-sg="weight-out"]');
    var presetButtons = container.querySelectorAll('[data-sg-preset]');
    var ownInput = container.querySelector('[data-sg="own-garment"]');
    var overrideInput = container.querySelector('[data-sg="chest-override"]');
    var unitWords = container.querySelectorAll('[data-sg-unit-word]');
    var resultEl = container.querySelector('[data-sg="result"]');
    var svg = container.querySelector('.sg-yourfit-svg');

    // internal state is always inches / lb. Default body = the ANSUR II
    // MEDIAN stature and weight for this body (unisex: male 69.1 in, 186.5 lb;
    // women's: female 64 in, 147.3 lb), the same on every Artifact. A saved
    // value from a previous visit on this device takes precedence.
    var heightIn = num(saved.heightIn) || g.medianStatureIn;
    var weightLb = num(saved.weightLb) || g.medianWeightLb;
    var preset = saved.preset || 'average';
    var ownFlatIn = num(saved.ownFlatIn);
    var overrideIn = num(saved.overrideIn);

    if (heightSlider) heightSlider.value = Math.round(heightIn);
    if (weightSlider) weightSlider.value = Math.round(weightLb);
    presetButtons.forEach(function (b) { b.classList.toggle('is-active', b.dataset.sgPreset === preset); });

    function toUnit(inches) { return inches == null ? '' : round1(state.unit === 'cm' ? inches * IN_TO_CM : inches); }
    function fromUnit(v) { var n = num(v); return n == null || n <= 0 ? null : (state.unit === 'cm' ? n / IN_TO_CM : n); }
    function showInputs() {
      if (ownInput) {
        ownInput.value = toUnit(ownFlatIn);
        ownInput.placeholder = state.unit === 'cm' ? 'e.g. 56' : 'e.g. 22';
      }
      if (overrideInput) overrideInput.value = toUnit(overrideIn);
      unitWords.forEach(function (w) { w.textContent = state.unit; });
    }
    showInputs();

    // persist: true only on real input, so an untouched default is never
    // saved and carried onto an Artifact that uses the other body.
    function recompute(persist) {
      heightIn = heightSlider ? Number(heightSlider.value) : heightIn;
      weightLb = weightSlider ? Number(weightSlider.value) : weightLb;
      var active = container.querySelector('[data-sg-preset].is-active');
      preset = active ? active.dataset.sgPreset : 'average';
      if (ownInput) ownFlatIn = fromUnit(ownInput.value);
      if (overrideInput) overrideIn = fromUnit(overrideInput.value);

      if (heightOut) heightOut.textContent = fmtHeight(heightIn, state.unit);
      if (weightOut) weightOut.textContent = fmtWeight(weightLb, state.unit);

      var body = estimateBody(g, heightIn, weightLb, preset);
      if (overrideIn != null) {
        // a measured circumference beats the estimate; scale the drawn breadth with it
        if (parsed.isPants) {
          body.hipBreadthIn *= overrideIn / body.hipIn;
          body.hipIn = overrideIn;
        } else {
          body.chestBreadthIn *= overrideIn / body.chestIn;
          body.bideltoidIn *= Math.sqrt(overrideIn / body.chestIn);
          body.chestIn = overrideIn;
        }
      }

      var rec = recommend(parsed, body, popBody, ownFlatIn);
      if (svg) drawFigure(svg, body, rec ? rec.size : null, { unit: state.unit });

      if (resultEl) {
        if (!rec) {
          resultEl.textContent = 'No size to compare yet.';
        } else {
          var f = fitFor(rec.size, body);
          var detail = rec.byOwnGarment
            ? 'Closest to your ' + fmtLen(ownFlatIn, state.unit) + ' flat.'
            : (f.fitBand ? f.fitBand + ', ' + fmtSigned(f.easeIn, state.unit) + ' ' + f.easeLabel + ' on you.' : '');
          resultEl.innerHTML = 'Recommended: <strong>' + String(rec.size.size) + '</strong>. ' + detail;
        }
      }
      onRecommend(rec, persist === true);

      if (persist === true) {
        safeLocalSet(STORAGE_KEY, {
          heightIn: heightIn, weightLb: weightLb, preset: preset,
          ownFlatIn: ownFlatIn, overrideIn: overrideIn,
        });
      }
    }
    var onInput = function () { recompute(true); };

    if (heightSlider) heightSlider.addEventListener('input', onInput);
    if (weightSlider) weightSlider.addEventListener('input', onInput);
    if (ownInput) ownInput.addEventListener('input', onInput);
    if (overrideInput) overrideInput.addEventListener('input', onInput);
    presetButtons.forEach(function (b) {
      b.addEventListener('click', function () {
        presetButtons.forEach(function (x) { x.classList.remove('is-active'); });
        b.classList.add('is-active');
        recompute(true);
      });
    });

    recompute(false);

    return {
      unitChanged: function () {
        showInputs();
        recompute(false);
      },
    };
  }

  function init() {
    document.querySelectorAll('.size-guide-modal__content[data-sg-root]:not([data-sg-init="1"])').forEach(initInstance);
    fitSubLines();
  }
  // The YOUR FIT sub-line is kept only when it fits on one line: measured
  // against the button's inner width, re-checked on resize and on open.
  function fitSubLines() {
    document.querySelectorAll('.sg-yourfit-summary__sub').forEach(function (sub) {
      var btn = sub.closest('.sg-yourfit-summary');
      if (!btn) return;
      sub.removeAttribute('data-sg-hidden');
      var inner = btn.clientWidth - 32; // button side padding
      if (inner > 0 && sub.scrollWidth > inner) sub.setAttribute('data-sg-hidden', '1');
    });
  }
  window.addEventListener('resize', fitSubLines);
  // Re-sync the highlight on every initialised root (size may have changed
  // without a re-render, e.g. the picker's preselection path).
  function syncHighlights() {
    document.querySelectorAll('.size-guide-modal__content[data-sg-root][data-sg-init="1"]').forEach(function (root) {
      if (typeof root.__sgApplyHighlight === 'function') root.__sgApplyHighlight();
    });
  }
  var scheduled = false;
  function scheduleInit() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; init(); syncHighlights(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Symmetry fires this after it swaps the variant markup (variant-picker.js).
  document.addEventListener('on:variant:change', scheduleInit);
  // Any other DOM swap of the block (quick buy, section rerender, editor).
  if (typeof MutationObserver === 'function') {
    new MutationObserver(scheduleInit).observe(document.documentElement, { childList: true, subtree: true });
  }
  // Belt and braces: make sure the guide is live the moment it is opened.
  document.addEventListener('click', function (evt) {
    if (evt.target.closest && evt.target.closest('.size-guide-trigger')) { init(); syncHighlights(); setTimeout(fitSubLines, 50); }
  });
  // Back/forward navigation (bfcache restores) and ?variant= loads.
  window.addEventListener('pageshow', function () { init(); syncHighlights(); });
})();
